<template>
  <div class="sk-root">
    <v-row no-gutters class="itf-main-row">

      <!-- LEFT: clock panel -->
      <v-col cols="12" md="5" class="itf-col-left sk-left">
        <div class="sk-clock shimmer"></div>
        <div class="sk-hms">
          <div class="sk-hms-cell shimmer" v-for="n in 3" :key="n" :style="{ animationDelay: `${n * 0.1}s` }"></div>
        </div>
        <div class="sk-progress shimmer" style="animation-delay: 0.2s"></div>
        <div class="sk-btn shimmer" style="animation-delay: 0.3s"></div>
      </v-col>

      <!-- RIGHT: detail panel — mirrors itf-col-right > idp-wrap -->
      <v-col cols="12" md="7" class="itf-col-right">
        <div class="sk-right">

          <!-- idp-header-section -->
          <div class="sk-header">
            <div class="sk-meta-top">
              <div class="sk-badge shimmer" v-for="n in 3" :key="n" :style="{ animationDelay: `${n * 0.08}s` }"></div>
            </div>
            <div class="sk-title shimmer" style="animation-delay: 0.15s"></div>
            <div class="sk-one-line shimmer" style="animation-delay: 0.2s"></div>
          </div>

          <!-- idp-divider -->
          <div class="sk-divider shimmer"></div>

          <!-- idp-detail-section: v-row > v-col cols=12 sm=6 pa-1 -->
          <div class="sk-detail-section">
            <v-row class="ma-0">
              <v-col
                v-for="n in 11"
                :key="n"
                cols="12"
                sm="6"
                class="pa-1"
              >
                <div class="sk-detail-card sk-stagger" :style="{ animationDelay: `${0.05 * n}s` }">
                  <div class="sk-detail-icon shimmer"></div>
                  <div class="sk-detail-body">
                    <div class="sk-detail-label shimmer"></div>
                    <div class="sk-detail-value shimmer"></div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </div>

          <!-- description placeholder -->
          <div class="sk-divider shimmer" style="margin-top: 20px"></div>
          <div class="sk-desc">
            <div class="sk-desc-label shimmer"></div>
            <div class="sk-desc-block shimmer"></div>
          </div>

        </div>
      </v-col>

    </v-row>
  </div>
</template>

<script>
export default { name: 'IssueTimerFocusSkeleton' };
</script>

<style scoped>
/* ── shimmer wave — refined gradient ─────────────────── */
@keyframes sk-wave {
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
}
.shimmer {
  background: linear-gradient(
    90deg,
    #f1f5f9 0%,
    #e8eef6 25%,
    #dfe7f2 50%,
    #e8eef6 75%,
    #f1f5f9 100%
  );
  background-size: 600px 100%;
  animation: sk-wave 1.8s ease-in-out infinite;
  border-radius: inherit;
}

/* ── staggered entrance ──────────────────────────────── */
@keyframes sk-fade-up {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.sk-stagger {
  opacity: 0;
  animation: sk-fade-up 0.4s ease forwards, sk-wave 1.8s ease-in-out infinite;
}

/* ── root ─────────────────────────────────────────────── */
.sk-root {
  flex: 1;
  overflow: hidden;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
}

/* ── LEFT panel — mirrors tcp-wrap ───────────────────── */
.sk-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 24px 32px 28px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
  background: linear-gradient(170deg, #ffffff 0%, #eef2ff 55%, #f0fdfa 100%);
  border-right: 1px solid rgba(226, 232, 240, 0.8);
}
.sk-clock    { width: 260px; height: 260px; border-radius: 50%; flex-shrink: 0; opacity: 0.85; }
.sk-hms      { display: flex; gap: 10px; width: 100%; max-width: 300px; }
.sk-hms-cell { flex: 1; height: 64px; border-radius: 14px; }
.sk-progress { width: 100%; max-width: 300px; height: 54px; border-radius: 14px; }
.sk-btn      { width: 180px; height: 44px; border-radius: 99px; }

/* ── RIGHT panel — mirrors .idp-wrap (nested inside itf-col-right) ── */
.sk-right {
  display: flex;
  flex-direction: column;
  gap: 0;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px 28px;
  box-sizing: border-box;
  background: #fff;
}
.sk-right::-webkit-scrollbar { width: 5px; }
.sk-right::-webkit-scrollbar-track { background: transparent; }
.sk-right::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 8px; }

/* header — mirrors idp-header-section */
.sk-header {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 4px;
}
.sk-meta-top {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.sk-badge    { width: 76px; height: 22px; border-radius: 99px; }
.sk-title    { height: 24px; width: 65%; border-radius: 8px; }
.sk-one-line { height: 14px; width: 40%; border-radius: 6px; }

/* divider — mirrors idp-divider */
.sk-divider {
  height: 1px;
  width: 100%;
  margin: 16px 0;
  border-radius: 0;
  opacity: 0.5;
  flex-shrink: 0;
}

/* detail section — uses Vuetify v-row/v-col for exact alignment */
.sk-detail-section {
  /* Vuetify handles the grid */
}

/* card — mirrors idp-detail-card */
.sk-detail-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fafbff;
  border: 1px solid #f1f5f9;
  box-sizing: border-box;
}

/* icon — mirrors idp-detail-icon: 28x28, border-radius 8px */
.sk-detail-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  flex-shrink: 0;
  margin-top: 1px;
}

/* body — mirrors idp-detail-body */
.sk-detail-body {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
  min-width: 0;
  padding-top: 1px;
}
.sk-detail-label { height: 9px;  width: 50%; border-radius: 4px; }
.sk-detail-value { height: 13px; width: 78%; border-radius: 6px; }

/* description placeholder */
.sk-desc {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 6px;
}
.sk-desc-label { height: 12px; width: 90px; border-radius: 4px; }
.sk-desc-block { height: 80px; width: 100%; border-radius: 12px; }

/* ── responsive ──────────────────────────────────────── */
@media (max-width: 959px) {
  .sk-left  { height: auto; border-right: none; border-bottom: 1px solid #e2e8f0; justify-content: flex-start; }
  .sk-right { height: auto; }
}
</style>
